/**
 * Canonical viewport breakpoints (Phase 171).
 *
 * Mirrors the comment block at the top of src/app/styles/tokens.css — CSS
 * cannot read a custom property inside `@media`, so the numbers live twice and
 * this file is the JS half. Change both or neither.
 */
export const BREAKPOINTS = { sm: 480, md: 640, lg: 900 } as const;
export type BreakpointName = keyof typeof BREAKPOINTS;

/** The exact media-query strings the CSS uses, so JS and CSS never disagree. */
export const MEDIA = {
  belowSm: '(max-width: 479px)',
  belowMd: '(max-width: 639px)',
  belowLg: '(max-width: 899px)',
  /**
   * The shell's handheld switch — sidebar vs. top bar + drawer + tab bar.
   *
   * NOT the same thing as `belowLg`, and the difference is a real bug we
   * shipped: width alone is a number the browser is willing to lie about.
   * Chrome's per-site "Desktop site" toggle carries into the installed WebAPK
   * and hands the PWA a ~980px layout viewport with the viewport meta ignored,
   * so a phone got the full desktop sidebar on every screen. A big phone in
   * landscape (iPhone 16 Pro Max is 956px) does the same thing honestly.
   *
   * So the second half asks what the DEVICE is rather than how wide it claims
   * to be: coarse primary pointer plus no hover is a phone or a tablet and
   * nothing else — a touchscreen laptop still reports `fine`/`hover`, so it
   * keeps the sidebar. The 1279px cap keeps a genuinely large touch display
   * (a 1280+ tablet in landscape, a kiosk panel) on the desktop shell, where
   * there is room for it.
   *
   * Mirrored verbatim in every `@media (max-width: 899px)` shell block and in
   * the `below-lg` custom variant in src/index.css. Change all of them or none.
   */
  handheld: '(max-width: 899px), (pointer: coarse) and (hover: none) and (max-width: 1279px)',
  coarsePointer: '(pointer: coarse)',
  noHover: '(hover: none)',
  reducedMotion: '(prefers-reduced-motion: reduce)',
  standalone: '(display-mode: standalone)',
} as const;
export type MediaQuery = (typeof MEDIA)[keyof typeof MEDIA];
